'use client';
import React from 'react';
import Image from 'next/image';

interface TeamBioModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: {
    name: string;
    role: string;
    bio: string;
  };
}

const TeamBioModal: React.FC<TeamBioModalProps> = ({ isOpen, onClose, member }) => {
  if (!isOpen) return null;

  // Handle backdrop click to close modal
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key to close modal
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  return (
    <div data-component-id="src/components/ui/TeamBioModal.tsx:46:4" data-component-path="src/components/ui/TeamBioModal.tsx" data-component-line="46" data-component-end-line="86" data-component-file="TeamBioModal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20inset-0%20z-50%20flex%20items-center%20justify-center%22%7D"
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(21, 10, 50, 0.9)' }}
      onClick={handleBackdropClick}
    >
      <div data-component-id="src/components/ui/TeamBioModal.tsx:51:6" data-component-path="src/components/ui/TeamBioModal.tsx" data-component-line="51" data-component-end-line="85" data-component-file="TeamBioModal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-secondary%20rounded-%5B12px%5D%20sm%3Arounded-%5B24px%5D%20p-4%20sm%3Ap-12%20max-w-%5B650px%5D%20w-full%20max-h-%5B80vh%5D%20mx-4%20flex%20flex-col%22%7D"
        className="bg-secondary rounded-[12px] sm:rounded-[24px] p-4 sm:p-12 max-w-[650px] w-full max-h-[80vh] mx-4 flex flex-col"
        style={{
          boxShadow:
            '117px 100px 43px 0px rgba(179, 179, 179, 0.00), 75px 64px 39px 0px rgba(179, 179, 179, 0.01), 42px 36px 33px 0px rgba(179, 179, 179, 0.05), 19px 16px 25px 0px rgba(179, 179, 179, 0.09), 5px 4px 14px 0px rgba(179, 179, 179, 0.10)',
        }}
      >
        {/* Header with name and close button */}
        <div data-component-id="src/components/ui/TeamBioModal.tsx:59:8" data-component-path="src/components/ui/TeamBioModal.tsx" data-component-line="59" data-component-end-line="70" data-component-file="TeamBioModal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-between%20items-start%20mb-1%20flex-shrink-0%22%7D" className="flex justify-between items-start mb-1 flex-shrink-0">
          <h2 data-component-id="src/components/ui/TeamBioModal.tsx:60:10" data-component-path="src/components/ui/TeamBioModal.tsx" data-component-line="60" data-component-end-line="62" data-component-file="TeamBioModal.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-text-primary%20font-bold%20font-bricolage%20text-%5B24px%5D%20leading-%5B28px%5D%22%7D" className="text-text-primary font-bold font-bricolage text-[24px] leading-[28px]">
            {member.name}
          </h2>
          <button data-component-id="src/components/ui/TeamBioModal.tsx:63:10" data-component-path="src/components/ui/TeamBioModal.tsx" data-component-line="63" data-component-end-line="69" data-component-file="TeamBioModal.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex-shrink-0%20hover%3Aopacity-70%20transition-opacity%22%7D"
            onClick={onClose}
            className="flex-shrink-0 hover:opacity-70 transition-opacity"
            aria-label="Close modal"
          >
            <Image data-component-id="src/components/ui/TeamBioModal.tsx:68:12" data-component-path="src/components/ui/TeamBioModal.tsx" data-component-line="68" data-component-end-line="68" data-component-file="TeamBioModal.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22src%22%3A%22%2Fimages%2Fx.svg%22%2C%22alt%22%3A%22Close%22%2C%22className%22%3A%22w-6%20h-6%22%7D" src="/images/x.svg" alt="Close" width={24} height={24} className="w-6 h-6" />
          </button>
        </div>

        {/* Role */}
        <p data-component-id="src/components/ui/TeamBioModal.tsx:73:8" data-component-path="src/components/ui/TeamBioModal.tsx" data-component-line="73" data-component-end-line="75" data-component-file="TeamBioModal.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%20text-%5B18px%5D%20font-normal%20leading-%5B28px%5D%20mb-6%20flex-shrink-0%22%7D" className="text-text-secondary text-[18px] font-normal leading-[28px] mb-6 flex-shrink-0">
          {member.role}
        </p>

        {/* Bio text - Scrollable area */}
        <div data-component-id="src/components/ui/TeamBioModal.tsx:78:8" data-component-path="src/components/ui/TeamBioModal.tsx" data-component-line="78" data-component-end-line="84" data-component-file="TeamBioModal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-text-primary%20text-%5B16px%5D%20font-normal%20leading-%5B28px%5D%20tracking-%5B0.16px%5D%20overflow-y-auto%20custom-scrollbar%20flex-1%20pr-2%22%7D" className="text-text-primary text-[16px] font-normal leading-[28px] tracking-[0.16px] overflow-y-auto custom-scrollbar flex-1 pr-2">
          {member.bio.split('\n').map((paragraph, index) => (
            <p data-component-id="src/components/ui/TeamBioModal.tsx:80:12" data-component-path="src/components/ui/TeamBioModal.tsx" data-component-line="80" data-component-end-line="82" data-component-file="TeamBioModal.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mb-2%20last%3Amb-0%22%7D" key={index} className="mb-2 last:mb-0">
              {paragraph}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TeamBioModal;
